import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { scriptsAPI, agentsAPI, phonenumbersAPI, knowledgeAPI, chatbotAPI, timeAgo, scriptKind } from './api';
import { placeTestCall, DEFAULT_FROM_NUMBER } from './testHelpers';
import { getOutboundCallBaseUrl, getCurrentUserIdForCalls } from '../../lib/apiConfig';
import BrowserVoiceWidget from '../../components/BrowserVoiceWidget';
import LiveTranscript from './LiveTranscript';
import { useAuth } from '../../context/AuthContext';
import {
  Bot,
  Upload,
  Plus,
  MoreHorizontal,
  Phone,
  MessageSquare,
  MessagesSquare,
  Mail,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  Wand2,
  FileText,
  GitBranch,
  Folder,
  Info,
  Pencil,
  CheckCircle2,
  Send,
  MessageCircle,
  Trash2,
  X,
  Heart,
  ShieldCheck,
  CalendarDays,
  Building2,
  GraduationCap,
  SlidersHorizontal,
  Search,
  Mic,
  Sparkles,
} from 'lucide-react';
import { Card, Badge, StatusDot, PrimaryButton, SecondaryButton, PageTitle } from './ui';
import { AGENT_VOICE_OPTIONS as VOICE_OPTIONS, formatAgentVoiceLabel } from '../../constants/agentVoices';

/* =========================================================
   Customer AI — 4 sub-menu pages
   1. Basic Settings   (identity + phone numbers)
   2. Script Building  (list of scripts)
   3. Knowledge & Connections
   4. Testing          (test each channel)
   ========================================================= */

/* ================= 1. Basic Settings ================= */

export const DemoAIBasicSettings = () => {
  const [numbers, setNumbers] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([phonenumbersAPI.list(), scriptsAPI.list(), agentsAPI.list()])
      .then(([numsRes, scriptsRes, agentsRes]) => {
        setNumbers(numsRes.data.numbers || []);
        setScripts(scriptsRes.data.scripts || []);
        setAgents(agentsRes.data.agents || []);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const scriptName = (id) => {
    if (!id) return null;
    const s = scripts.find((x) => String(x._id) === String(id));
    return s ? s.scriptName : null;
  };
  const agentName = (id) => {
    if (!id) return null;
    const a = agents.find((x) => String(x._id) === String(id));
    return a ? a.name : null;
  };

  const handleAdd = async () => {
    if (!newNumber.trim()) return;
    setSaving(true);
    try {
      const res = await phonenumbersAPI.create({
        number: newNumber.trim(),
        friendlyName: newName.trim() || undefined,
      });
      setNumbers((ns) => [...(ns || []), res.data.number]);
      setNewNumber('');
      setNewName('');
      setAdding(false);
    } catch (e) {
      alert(`Failed to add number: ${e.response?.data?.error || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (n) => {
    if (!window.confirm(`Remove ${n.number}?`)) return;
    try {
      await phonenumbersAPI.delete(n._id);
      setNumbers((ns) => ns.filter((x) => x._id !== n._id));
    } catch (e) {
      alert(`Failed to remove: ${e.response?.data?.error || e.message}`);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageTitle
        title="Phone Numbers"
        subtitle="Manage the phone numbers your Customer AI answers and calls from."
      />

      <div className="mt-6 flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Phone Numbers */}
        <Card className="max-h-full overflow-y-auto p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[16px] font-semibold">Phone Numbers</h2>
              <p className="mt-1 text-[13px] text-slate-500">
                Assign the numbers this Customer AI will call and text from.
              </p>
            </div>
            <button
              onClick={() => setAdding((a) => !a)}
              className="flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-[12.5px] font-semibold text-teal-700 hover:bg-teal-100"
            >
              <Plus size={14} /> Add Number
            </button>
          </div>

          {error && <div className="mt-4"><ErrorNote error={error} /></div>}
          {!error && numbers === null && <LoadingRows label="Loading phone numbers…" />}
          {!error && numbers !== null && numbers.length === 0 && !adding && (
            <p className="py-8 text-center text-[13.5px] text-slate-400">
              No phone numbers yet — add your first Twilio number.
            </p>
          )}

          <div className="mt-5 divide-y divide-slate-100">
            {(numbers || []).map((n) => {
              const inboundAgent = agentName(n.inboundAgentId);
              const outbound = scriptName(n.outboundScriptId);
              const shared = !n.ownerId;
              return (
                <div key={n._id} className="flex items-center gap-3.5 py-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                    <Phone size={18} strokeWidth={1.9} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-semibold">{n.number}</span>
                      {n.friendlyName && <Badge tone="teal">{n.friendlyName}</Badge>}
                      {shared && <Badge tone="gray">Shared</Badge>}
                      <Badge tone={n.status === 'active' ? 'green' : 'gray'}>
                        {n.status === 'active' ? 'Active' : n.status || 'Inactive'}
                      </Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-slate-500">
                      <span>Inbound agent: {inboundAgent || '—'}</span>
                      <span className="text-slate-300">•</span>
                      <span>Outbound: {outbound || '—'}</span>
                    </div>
                  </div>
                  {!shared && (
                    <button
                      onClick={() => handleDelete(n)}
                      title="Remove number"
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {adding ? (
            <div className="mt-3 space-y-2.5 rounded-xl border border-teal-200 bg-teal-50/30 p-4">
              <input
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
                placeholder="+1 415 555 0123"
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
              />
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Friendly name (optional)"
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
              />
              <div className="flex items-center gap-2.5">
                <PrimaryButton onClick={handleAdd} disabled={saving} className="px-4 py-2 text-[13px]">
                  {saving ? 'Adding…' : 'Add Number'}
                </PrimaryButton>
                <SecondaryButton onClick={() => setAdding(false)} className="px-4 py-2 text-[13px]">
                  Cancel
                </SecondaryButton>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-3 text-[13.5px] font-semibold text-slate-500 hover:border-teal-300 hover:text-teal-700"
            >
              <Plus size={16} /> Add Phone Number
            </button>
          )}

          <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-indigo-50/60 p-3.5 text-[12.5px] leading-relaxed text-slate-600">
            <Info size={15} className="mt-0.5 shrink-0 text-indigo-400" />
            <span>
              Numbers are provisioned through Twilio. A number can only be assigned to one Customer AI at a time.
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ================= 2. Script Building ================= */

export const LoadingRows = ({ label = 'Loading…' }) => (
  <div className="flex items-center justify-center gap-3 py-12 text-[13.5px] text-slate-400">
    <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-teal-600" />
    {label}
  </div>
);

export const ErrorNote = ({ error }) => (
  <div className="rounded-xl bg-red-50 px-4 py-3 text-[13px] text-red-600">
    Failed to load: {error}
  </div>
);

/* Settings card shared by the script editors — controlled; changes flow up via onChange
   and are persisted to the script's `settings` object by the editor's auto-save. */
const AGENT_MODEL_OPTIONS = [
  { value: 'claude-3-5-sonnet', label: 'Claude 3.5 Sonnet' },
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { value: 'claude-3-opus', label: 'Claude 3 Opus' },
];
const AGENT_TONE_OPTIONS = ['Friendly', 'Professional', 'Warm', 'Empathetic', 'Concise', 'Enthusiastic'];
// Voice / speech models — saved on the agent (source of truth for inbound calls).
const AGENT_TTS_PROVIDER_OPTIONS = [
  { value: '', label: 'Default (ElevenLabs)' },
  { value: 'elevenlabs', label: 'ElevenLabs (multilingual)' },
  { value: 'cartesia', label: 'Cartesia Sonic (multilingual)' },
  { value: 'kokoro', label: 'Kokoro (English, fastest)' },
];
const AGENT_TTS_MODEL_OPTIONS = [
  { value: '', label: 'Default (Flash v2.5)' },
  { value: 'eleven_flash_v2_5', label: 'ElevenLabs Flash v2.5 (fast)' },
  { value: 'eleven_turbo_v2_5', label: 'ElevenLabs Turbo v2.5' },
  { value: 'eleven_multilingual_v2', label: 'ElevenLabs Multilingual v2' },
  { value: 'eleven_v3', label: 'ElevenLabs v3 (highest quality)' },
];
const AGENT_STT_MODEL_OPTIONS = [
  { value: '', label: 'Default (gpt-transcribe)' },
  { value: 'gpt-transcribe', label: 'OpenAI gpt-transcribe (EN + 中文)' },
  { value: 'gpt-4o-transcribe', label: 'OpenAI gpt-4o-transcribe (higher accuracy)' },
  { value: 'nova-2-phonecall', label: 'Deepgram nova-2-phonecall (English)' },
  { value: 'nova-2', label: 'Deepgram nova-2 (incl. Chinese)' },
  { value: 'nova-3', label: 'Deepgram nova-3 (incl. Chinese)' },
];
const AGENT_LANGUAGE_OPTIONS = [
  { value: '', label: 'Auto / Default' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese (中文)' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'hi', label: 'Hindi' },
];
const AGENT_LENGTH_OPTIONS = [
  { value: 'short', label: 'Short (~500 tokens)' },
  { value: 'medium', label: 'Medium (2,000 tokens)' },
  { value: 'long', label: 'Long (4,000 tokens)' },
];
const AGENT_MEMORY_OPTIONS = [
  { value: 'long', label: 'Long-term memory (Recommended)' },
  { value: 'short', label: 'Short-term memory' },
  { value: 'none', label: 'No memory' },
];

const SettingSelect = ({ value, onChange, placeholder, children }) => (
  <div className="relative mt-1.5">
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 pr-9 text-[14px] outline-none focus:border-teal-500 hover:border-slate-300"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {children}
    </select>
    <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
  </div>
);

export const AgentSettingsCard = ({ value = {}, onChange = () => {} }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const temp = value.temperature ?? 0.3;
  return (
    <Card className="max-h-full overflow-y-auto p-6">
      <h2 className="text-[16px] font-semibold">Settings</h2>
      <p className="mt-1 text-[13px] text-slate-500">Configure how your AI Agent speaks and responds.</p>

      <div className="mt-5 space-y-5">
        <div>
          <label className="flex items-center gap-1.5 text-[13px] font-semibold">
            Model <Info size={13} className="text-slate-300" />
          </label>
          <SettingSelect value={value.model} onChange={(v) => set({ model: v })} placeholder="Default">
            {AGENT_MODEL_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </SettingSelect>
        </div>

        <div>
          <p className="text-[13px] font-semibold">Voice Settings</p>
          <label className="mt-3 block text-[12.5px] font-medium text-slate-500">Tone</label>
          <SettingSelect value={value.tone} onChange={(v) => set({ tone: v })} placeholder="Default">
            {AGENT_TONE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </SettingSelect>
          <label className="mt-3 block text-[12.5px] font-medium text-slate-500">Voice</label>
          <SettingSelect
            value={value.voiceId}
            onChange={(v) => {
              const opt = VOICE_OPTIONS.find((x) => x.id === v);
              set({ voiceId: v, voiceName: opt ? formatAgentVoiceLabel(opt) : '' });
            }}
            placeholder="Default"
          >
            {VOICE_OPTIONS.map((v) => (
              <option key={v.id} value={v.id}>{formatAgentVoiceLabel(v)}</option>
            ))}
          </SettingSelect>
        </div>

        <div>
          <p className="text-[13px] font-semibold">Response Settings</p>
          <label className="mt-3 flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500">
            Temperature <Info size={12} className="text-slate-300" />
          </label>
          <p className="text-[11.5px] text-slate-400">Controls randomness in responses.</p>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={1}
              step={0.1}
              value={temp}
              onChange={(e) => set({ temperature: Number(e.target.value) })}
              className="h-1.5 flex-1 accent-teal-600"
            />
            <span className="rounded-lg border border-slate-200 px-2.5 py-1 text-[13px] font-semibold">
              {Number(temp).toFixed(1)}
            </span>
          </div>
          <label className="mt-4 flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500">
            Max Response Length <Info size={12} className="text-slate-300" />
          </label>
          <SettingSelect value={value.maxResponseLength || 'medium'} onChange={(v) => set({ maxResponseLength: v })}>
            {AGENT_LENGTH_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SettingSelect>
        </div>

        <div>
          <label className="flex items-center gap-1.5 text-[13px] font-semibold">
            Memory <Info size={13} className="text-slate-300" />
          </label>
          <p className="text-[11.5px] text-slate-400">How much conversation history the AI should remember.</p>
          <SettingSelect value={value.memory || 'long'} onChange={(v) => set({ memory: v })}>
            {AGENT_MEMORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SettingSelect>
        </div>
      </div>
    </Card>
  );
};

/* ================= Agents (Customer AI list) =================
   An "agent" is a script plus its basic settings, phone assignment, and knowledge.
   We reuse the script record and enrich it client-side (/api/scripts is a summary).
   Status/channels are derived from phone assignment (not tracked on the record yet). */

const AGENT_PALETTE = [
  { icon: Heart, tint: 'bg-rose-50 text-rose-500' },
  { icon: Bot, tint: 'bg-teal-50 text-teal-600' },
  { icon: CalendarDays, tint: 'bg-orange-50 text-orange-500' },
  { icon: ShieldCheck, tint: 'bg-emerald-50 text-emerald-600' },
  { icon: Building2, tint: 'bg-sky-50 text-sky-600' },
  { icon: GraduationCap, tint: 'bg-violet-50 text-violet-600' },
];

const STATUS_TONE = { Active: 'green', Draft: 'blue', Paused: 'orange', Archived: 'gray' };

const AGENT_ICON_OPTIONS = [
  { key: 'bot', icon: Bot, tint: 'bg-teal-50 text-teal-600' },
  { key: 'heart', icon: Heart, tint: 'bg-rose-50 text-rose-500' },
  { key: 'shield', icon: ShieldCheck, tint: 'bg-emerald-50 text-emerald-600' },
  { key: 'calendar', icon: CalendarDays, tint: 'bg-orange-50 text-orange-500' },
  { key: 'building', icon: Building2, tint: 'bg-sky-50 text-sky-600' },
  { key: 'grad', icon: GraduationCap, tint: 'bg-violet-50 text-violet-600' },
  { key: 'chat', icon: MessageCircle, tint: 'bg-cyan-50 text-cyan-600' },
  { key: 'phone', icon: Phone, tint: 'bg-amber-50 text-amber-600' },
];
const iconOption = (key) => AGENT_ICON_OPTIONS.find((o) => o.key === key) || null;

const AGENT_STATUS_OPTIONS = ['active', 'draft', 'paused', 'archived'];

/* List-style single-select: clickable rows with a highlight + checkmark (no native dropdown).
   options: [{ value, label, sub? }]. Pass maxHeight (px) to make a long list scroll. */
const ListPicker = ({ options, value, onChange, columns = 1, maxHeight }) => {
  const grid = (
    <div className={`grid gap-2 ${columns === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
      {options.map((o) => {
        const on = String(value ?? '') === String(o.value);
        return (
          <button
            key={String(o.value)}
            type="button"
            onClick={() => onChange(o.value)}
            className={`flex items-center justify-between gap-2 rounded-xl border px-3.5 py-2.5 text-left transition-colors ${
              on ? 'border-teal-300 bg-teal-50/50' : 'border-slate-200 hover:bg-slate-50'
            }`}
          >
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold">{o.label}</span>
              {o.sub && <span className="block truncate text-[11.5px] text-slate-500">{o.sub}</span>}
            </span>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? 'border-teal-500 bg-teal-500 text-white' : 'border-slate-300'}`}>
              {on && <CheckCircle2 size={13} />}
            </span>
          </button>
        );
      })}
    </div>
  );
  return maxHeight ? (
    <div className="mt-2 overflow-y-auto pr-1" style={{ maxHeight }}>{grid}</div>
  ) : (
    <div className="mt-2">{grid}</div>
  );
};

/* Rich voice dropdown: a trigger showing the picked voice + a searchable popover
   grouped by provider (ElevenLabs / Kokoro). value/onChange is a voice id ('' = default). */
const VoiceSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = VOICE_OPTIONS.find((v) => v.id === value) || null;
  const query = q.trim().toLowerCase();
  const match = (v) => !query || `${v.name} ${v.language} ${v.accent} ${v.provider}`.toLowerCase().includes(query);
  const groups = [
    { key: 'ElevenLabs', label: 'ElevenLabs — higher quality', items: VOICE_OPTIONS.filter((v) => v.provider === 'ElevenLabs' && match(v)) },
    { key: 'Fish Audio', label: 'Fish Audio — clone / library, multilingual', items: VOICE_OPTIONS.filter((v) => v.provider === 'Fish Audio' && match(v)) },
    { key: 'Kokoro', label: 'Kokoro — fast, phone', items: VOICE_OPTIONS.filter((v) => v.provider === 'Kokoro' && match(v)) },
  ];
  const hasResults = groups.some((g) => g.items.length > 0);

  const choose = (id) => {
    onChange(id);
    setOpen(false);
    setQ('');
  };

  return (
    <div className="relative mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center gap-3 rounded-xl border bg-white px-3.5 py-2.5 text-left transition-colors ${
          open ? 'border-teal-500' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
          <Mic size={17} strokeWidth={1.9} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-semibold">{selected ? selected.name : 'Default voice'}</span>
          <span className="block truncate text-[11.5px] text-slate-500">
            {selected ? `${selected.language} · ${selected.accent} · ${selected.provider}` : 'Use the system default voice'}
          </span>
        </span>
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 z-40 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-100 p-2">
              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search voices…"
                  className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-2.5 text-[13px] outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="max-h-[280px] overflow-y-auto p-1.5">
              <button
                type="button"
                onClick={() => choose('')}
                className={`flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-[13px] ${
                  !value ? 'bg-teal-50 text-teal-700' : 'hover:bg-slate-50'
                }`}
              >
                <span className="font-semibold">Default voice</span>
                {!value && <CheckCircle2 size={15} className="text-teal-600" />}
              </button>
              {groups.map((g) =>
                g.items.length > 0 ? (
                  <div key={g.key} className="mt-1">
                    <p className="px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">{g.label}</p>
                    {g.items.map((v) => {
                      const on = v.id === value;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => choose(v.id)}
                          className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left ${on ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                        >
                          <span className="min-w-0">
                            <span className={`block truncate text-[13px] font-semibold ${on ? 'text-teal-700' : ''}`}>{v.name}</span>
                            <span className="block truncate text-[11px] text-slate-500">{v.language} · {v.accent}</span>
                          </span>
                          {on && <CheckCircle2 size={15} className="shrink-0 text-teal-600" />}
                        </button>
                      );
                    })}
                  </div>
                ) : null
              )}
              {!hasResults && <p className="px-2.5 py-6 text-center text-[12.5px] text-slate-400">No voices match “{q}”.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* Compact rich dropdown for short option lists (Model, Tone, …): styled trigger + a
   popover of rows with a checkmark. options: [{ value, label, sub? }] ('' = default). */
const FancySelect = ({ value, onChange, options, placeholder = 'Select…', icon: Icon }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => String(o.value) === String(value ?? '')) || null;
  const choose = (v) => {
    onChange(v);
    setOpen(false);
  };
  return (
    <div className="relative mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center gap-3 rounded-xl border bg-white px-3.5 py-2.5 text-left transition-colors ${
          open ? 'border-teal-500' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        {Icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
            <Icon size={17} strokeWidth={1.9} />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-semibold">{selected ? selected.label : placeholder}</span>
          {selected?.sub && <span className="block truncate text-[11.5px] text-slate-500">{selected.sub}</span>}
        </span>
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 z-40 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
            <div className="max-h-[280px] overflow-y-auto p-1.5">
              {options.map((o) => {
                const on = String(o.value) === String(value ?? '');
                return (
                  <button
                    key={String(o.value)}
                    type="button"
                    onClick={() => choose(o.value)}
                    className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left ${on ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                  >
                    <span className="min-w-0">
                      <span className={`block truncate text-[13px] font-semibold ${on ? 'text-teal-700' : ''}`}>{o.label}</span>
                      {o.sub && <span className="block truncate text-[11px] text-slate-500">{o.sub}</span>}
                    </span>
                    {on && <CheckCircle2 size={15} className="shrink-0 text-teal-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* Popup to edit an agent's three parts: basic settings (+ icon), knowledge base, and its linked script. */
const AgentEditModal = ({ agent, collections, onClose, onSaved }) => {
  const navigate = useNavigate();
  const s0 = agent.settings || {};
  const [tab, setTab] = useState('basic'); // basic | knowledge | script
  const [name, setName] = useState(agent.name || '');
  const [desc, setDesc] = useState(agent.description || '');
  const [icon, setIcon] = useState(agent.icon || 'bot');
  const [status, setStatus] = useState(agent.status || 'draft');
  const [voiceId, setVoiceId] = useState(s0.voiceId || '');
  const [ttsProvider, setTtsProvider] = useState(s0.ttsProvider || '');
  const [ttsModel, setTtsModel] = useState(s0.ttsModel || '');
  const [sttModel, setSttModel] = useState(s0.sttModel || '');
  const [sttLanguage, setSttLanguage] = useState(s0.sttLanguage || '');
  const [ttsLanguage, setTtsLanguage] = useState(s0.ttsLanguage || '');
  const [model, setModel] = useState(s0.model || '');
  const [tone, setTone] = useState(s0.tone || '');
  const [rules, setRules] = useState(s0.systemPrompt || '');
  const [collectionIds, setCollectionIds] = useState((agent.collectionIds || []).map(String));

  // The script this agent runs — chosen from existing scripts (not created here).
  const [scriptId, setScriptId] = useState(agent.scriptId ? String(agent.scriptId) : '');
  const [scripts, setScripts] = useState(null);
  const [scriptQuery, setScriptQuery] = useState('');
  const [scriptType, setScriptType] = useState('all'); // all | Prompt | Workflow
  const [kbQuery, setKbQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    scriptsAPI
      .list()
      .then((r) => setScripts(r.data.scripts || []))
      .catch(() => setScripts([]));
  }, []);

  const toggleCol = (id) =>
    setCollectionIds((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const isNew = !agent._id;

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const voiceName = VOICE_OPTIONS.find((v) => v.id === voiceId)?.name || s0.voiceName || '';
      const settings = { ...s0, voiceId, voiceName, ttsProvider, ttsModel, sttModel, sttLanguage, ttsLanguage, model, tone, systemPrompt: rules };
      const linkedScript = scriptId || null;
      const payload = { name: name || 'Untitled', description: desc, icon, settings, collectionIds, status, scriptId: linkedScript };
      if (isNew) {
        const res = await agentsAPI.save(payload);
        const created = res.data.agent || { ...payload, _id: res.data.id };
        onSaved(created);
      } else {
        await agentsAPI.update({ _id: agent._id, ...payload });
        onSaved({ ...agent, ...payload });
      }
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const field = 'mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500';
  const TABS = [
    { key: 'basic', label: 'Basic Settings' },
    { key: 'rules', label: 'Rules' },
    { key: 'knowledge', label: 'Knowledge Base' },
    { key: 'script', label: 'Script' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div
        className="flex h-[92vh] max-h-[880px] w-full max-w-[860px] flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-[16px] font-semibold">{isNew ? 'Create agent' : 'Edit agent'}</h3>
            <p className="text-[12.5px] text-slate-500">{name || agent.name || 'New Customer AI'}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex shrink-0 items-center gap-1 border-b border-slate-100 px-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-3.5 py-2.5 text-[13px] font-semibold transition-colors ${
                tab === t.key ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {tab === 'basic' && (
            <div className="space-y-4">
              <div>
                <label className="text-[12.5px] font-semibold text-slate-600">Icon</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {AGENT_ICON_OPTIONS.map(({ key, icon: Icon, tint }) => (
                    <button
                      key={key}
                      onClick={() => setIcon(key)}
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${tint} ring-2 transition ${
                        icon === key ? 'ring-teal-500' : 'ring-transparent hover:ring-slate-200'
                      }`}
                    >
                      <Icon size={18} strokeWidth={1.9} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-slate-600">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-slate-600">Description</label>
                <input
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  placeholder="What this agent does…"
                  className={field}
                />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-slate-600">Voice</label>
                <VoiceSelect value={voiceId} onChange={setVoiceId} />
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                <p className="mb-2 text-[12.5px] font-semibold text-slate-700">Speech &amp; voice models</p>
                <p className="mb-3 text-[11px] text-slate-500">Used when this agent answers an inbound number. A script&apos;s own settings override these.</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="text-[12px] font-semibold text-slate-600">TTS engine</label>
                    <FancySelect value={ttsProvider} onChange={setTtsProvider} options={AGENT_TTS_PROVIDER_OPTIONS} />
                  </div>
                  {ttsProvider === 'elevenlabs' && (
                    <div>
                      <label className="text-[12px] font-semibold text-slate-600">ElevenLabs model</label>
                      <FancySelect value={ttsModel} onChange={setTtsModel} options={AGENT_TTS_MODEL_OPTIONS} />
                    </div>
                  )}
                  {ttsProvider === 'cartesia' && (
                    <div>
                      <label className="text-[12px] font-semibold text-slate-600">Cartesia language</label>
                      <FancySelect value={ttsLanguage} onChange={setTtsLanguage} options={AGENT_LANGUAGE_OPTIONS} />
                    </div>
                  )}
                  <div>
                    <label className="text-[12px] font-semibold text-slate-600">Speech recognition (STT)</label>
                    <FancySelect value={sttModel} onChange={setSttModel} options={AGENT_STT_MODEL_OPTIONS} />
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-slate-600">STT language</label>
                    <FancySelect value={sttLanguage} onChange={setSttLanguage} options={AGENT_LANGUAGE_OPTIONS} />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-[12.5px] font-semibold text-slate-600">Model</label>
                  <FancySelect
                    icon={Wand2}
                    value={model}
                    onChange={setModel}
                    options={[{ value: '', label: 'Default' }, ...AGENT_MODEL_OPTIONS.map((m) => ({ value: m.value, label: m.label }))]}
                  />
                </div>
                <div>
                  <label className="text-[12.5px] font-semibold text-slate-600">Tone</label>
                  <FancySelect
                    icon={MessageCircle}
                    value={tone}
                    onChange={setTone}
                    options={[{ value: '', label: 'Default' }, ...AGENT_TONE_OPTIONS.map((t) => ({ value: t, label: t }))]}
                  />
                </div>
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-slate-600">Status</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {AGENT_STATUS_OPTIONS.map((sKey) => (
                    <button
                      key={sKey}
                      onClick={() => setStatus(sKey)}
                      className={`rounded-full border px-3.5 py-1.5 text-[12.5px] font-semibold capitalize transition-colors ${
                        status === sKey
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {sKey}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'rules' && (
            <div>
              <div className="flex items-start gap-2.5 rounded-xl bg-indigo-50/60 p-3.5 text-[12.5px] leading-relaxed text-slate-600">
                <ShieldCheck size={15} className="mt-0.5 shrink-0 text-indigo-400" />
                <span>Rules are the agent's overall system prompt — guardrails it must always follow, on top of the script.</span>
              </div>
              <label className="mt-4 block text-[12.5px] font-semibold text-slate-600">Overall rules (system prompt)</label>
              <textarea
                value={rules}
                onChange={(e) => setRules(e.target.value)}
                rows={12}
                placeholder={'e.g.\n- Always stay polite and professional.\n- Never share internal or personal data.\n- If unsure, offer to connect the caller with a human.\n- Never make up prices, availability, or policies.'}
                className="mt-1.5 w-full resize-none rounded-xl border border-slate-200 px-3.5 py-3 text-[13.5px] leading-relaxed outline-none focus:border-teal-500"
              />
            </div>
          )}

          {tab === 'knowledge' && (
            <div>
              <div className="flex items-start justify-between gap-3">
                <p className="text-[12.5px] text-slate-500">Choose which knowledge collections this agent can use.</p>
                <button
                  onClick={() => navigate('/dashboard/customer-ai/knowledge')}
                  className="inline-flex shrink-0 items-center gap-1.5 text-[12.5px] font-semibold text-teal-700 hover:text-teal-800"
                >
                  Manage knowledge base <ArrowRight size={13} />
                </button>
              </div>
              <div className="relative mt-3">
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={kbQuery}
                  onChange={(e) => setKbQuery(e.target.value)}
                  placeholder="Search collections…"
                  className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-[13.5px] outline-none focus:border-teal-500"
                />
              </div>
              <div className="mt-3 space-y-2">
                {(collections || []).length === 0 && (
                  <p className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-[13px] text-slate-400">
                    No knowledge collections yet — add one in Knowledge & Connections.
                  </p>
                )}
                {(() => {
                  const q = kbQuery.trim().toLowerCase();
                  const filtered = (collections || []).filter((c) => !q || (c.name || '').toLowerCase().includes(q));
                  if ((collections || []).length > 0 && filtered.length === 0) {
                    return (
                      <p className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-[13px] text-slate-400">
                        No collections match “{kbQuery}”.
                      </p>
                    );
                  }
                  return filtered.map((c) => {
                  const on = collectionIds.includes(String(c._id));
                  return (
                    <button
                      key={c._id}
                      onClick={() => toggleCol(String(c._id))}
                      className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                        on ? 'border-teal-300 bg-teal-50/50' : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${on ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
                        <Folder size={16} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-semibold">{c.name}</span>
                        <span className="block text-[12px] text-slate-500">
                          {c.fileCount ?? (c.fileIds || []).length} files
                        </span>
                      </span>
                      <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${on ? 'border-teal-500 bg-teal-500 text-white' : 'border-slate-300'}`}>
                        {on && <CheckCircle2 size={13} />}
                      </span>
                    </button>
                  );
                  });
                })()}
              </div>
            </div>
          )}

          {tab === 'script' && (
            <div className="space-y-3">
              <p className="text-[12.5px] text-slate-500">
                Choose the script (conversation) this agent runs. Create or edit scripts in Script Building.
              </p>
              {scripts !== null && scripts.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={scriptQuery}
                      onChange={(e) => setScriptQuery(e.target.value)}
                      placeholder="Search scripts…"
                      className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-[13.5px] outline-none focus:border-teal-500"
                    />
                  </div>
                  <div className="flex shrink-0 items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
                    {[
                      ['all', 'All'],
                      ['Prompt', 'Prompt'],
                      ['Workflow', 'Workflow'],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => setScriptType(key)}
                        className={`rounded-lg px-2.5 py-1.5 text-[12px] font-semibold transition-colors ${
                          scriptType === key ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {scripts === null ? (
                <p className="py-10 text-center text-[13px] text-slate-400">Loading scripts…</p>
              ) : scripts.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-[13px] text-slate-400">
                  No scripts yet — create one in Script Building.
                </p>
              ) : (() => {
                const q = scriptQuery.trim().toLowerCase();
                const filtered = scripts.filter((sc) => {
                  const matchesQ = !q || (sc.scriptName || '').toLowerCase().includes(q);
                  const matchesType = scriptType === 'all' || scriptKind(sc.type) === scriptType;
                  return matchesQ && matchesType;
                });
                if (filtered.length === 0) {
                  return (
                    <p className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-[13px] text-slate-400">
                      No scripts match your search.
                    </p>
                  );
                }
                return (
                <div className="space-y-2">
                  {filtered.map((sc) => {
                    const on = String(scriptId) === String(sc._id);
                    const isWf = scriptKind(sc.type) === 'Workflow';
                    return (
                      <button
                        key={sc._id}
                        onClick={() => setScriptId(on ? '' : String(sc._id))}
                        className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                          on ? 'border-teal-300 bg-teal-50/50' : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${isWf ? 'bg-violet-50 text-violet-600' : 'bg-sky-50 text-sky-600'}`}>
                          {isWf ? <GitBranch size={16} /> : <FileText size={16} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13.5px] font-semibold">{sc.scriptName || 'Untitled'}</span>
                          <span className="block text-[12px] text-slate-500">{isWf ? 'Conversation flow' : 'Text prompt'}</span>
                        </span>
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? 'border-teal-500 bg-teal-500 text-white' : 'border-slate-300'}`}>
                          {on && <CheckCircle2 size={13} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                );
              })()}
              {scriptId && (
                <button
                  onClick={() => navigate(`/dashboard/customer-ai/scripts/${scriptId}`)}
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-teal-700 hover:text-teal-800"
                >
                  Open in full editor <ArrowRight size={13} />
                </button>
              )}
            </div>
          )}

          {err && <p className="mt-3 text-[12.5px] text-red-600">{err}</p>}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : isNew ? 'Create agent' : 'Save changes'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
const AGENT_CHANNELS = [
  { key: 'voice', icon: Phone, color: 'text-teal-600' },
  { key: 'chat', icon: MessagesSquare, color: 'text-sky-600' },
  { key: 'sms', icon: MessageSquare, color: 'text-violet-600' },
  { key: 'email', icon: Mail, color: 'text-orange-500' },
];

/* Pre-built agent blueprints. Each instantiates a configured agent + script on "Use template". */
export const AGENT_TEMPLATES = [
  {
    key: 'hotel-front-desk',
    name: 'Hotel Front Desk',
    icon: 'building',
    category: 'Hospitality',
    description: 'Answer guest calls, manage reservations, and handle service requests.',
    tagline:
      'Your AI front desk answers guest calls, manages reservations, handles requests, and provides 24/7 guest service across every stay.',
    tags: ['Calls', 'SMS', 'Reservations', 'PMS'],
    capabilities: [
      { title: 'Manage Reservations', desc: 'Check availability and create or modify bookings.' },
      { title: 'Answer Guest Questions', desc: 'Amenities, hours, policies, and local information.' },
      { title: 'Handle Guest Requests', desc: 'Housekeeping, maintenance, wake-up calls, and more.' },
      { title: 'Support Check-in & Check-out', desc: 'Arrival details, confirmations, and departure support.' },
      { title: 'Route & Escalate', desc: 'Send urgent or complex issues to the right team.' },
    ],
    included: [
      'Pre-built conversation script',
      'Actions & permissions',
      'Escalation rules',
      'Test scenarios',
      'Best practice guidance',
    ],
    scenarios: [
      { title: 'Guest asks about room availability', tag: 'Reservations' },
      { title: 'Guest requests extra towels', tag: 'Guest Request' },
      { title: 'Guest asks about check-in time', tag: 'Guest Q&A' },
      { title: 'Guest reports an urgent maintenance issue', tag: 'Escalation' },
      { title: 'Guest wants to modify a booking', tag: 'Booking' },
    ],
    scriptType: 'Text Prompt',
    script: [
      'You are the AI front desk agent for a hotel. You are warm, professional, and efficient.',
      '',
      'Your responsibilities:',
      '- Greet guests and callers courteously and identify how you can help.',
      '- Check room availability and help create, confirm, or modify reservations.',
      '- Answer questions about amenities, hours, policies, directions, and local recommendations.',
      '- Take service requests (housekeeping, maintenance, wake-up calls) and confirm the details.',
      '- Help with check-in and check-out details and confirmations.',
      '- Escalate urgent safety, security, or maintenance issues to a human team member immediately.',
      '',
      'Always confirm names, dates, and key details back to the guest. Never invent availability,',
      'prices, or policies you were not given — offer to check with the team instead.',
    ].join('\n'),
    settings: { tone: 'Warm' },
  },
  {
    key: 'dental-receptionist',
    name: 'Dental Receptionist',
    icon: 'heart',
    category: 'Healthcare',
    description: 'Answer calls, schedule appointments, and answer common patient questions.',
    tagline:
      'Your AI receptionist answers patient calls, books and reschedules appointments, and answers common questions all day.',
    tags: ['Calls', 'SMS', 'Scheduling', 'FAQs'],
    capabilities: [
      { title: 'Schedule Appointments', desc: 'Book, reschedule, and confirm patient visits.' },
      { title: 'Answer Patient Questions', desc: 'Hours, insurance, services, and directions.' },
      { title: 'Collect Patient Info', desc: 'Capture name, contact, and reason for the visit.' },
      { title: 'Send Reminders', desc: 'Confirm upcoming appointments over SMS.' },
      { title: 'Route & Escalate', desc: 'Send clinical or urgent issues to staff.' },
    ],
    included: [
      'Pre-built conversation script',
      'Actions & permissions',
      'Escalation rules',
      'Test scenarios',
      'Best practice guidance',
    ],
    scenarios: [
      { title: 'Patient wants to book a cleaning', tag: 'Scheduling' },
      { title: 'Patient asks if you take their insurance', tag: 'FAQ' },
      { title: 'Patient needs to reschedule', tag: 'Scheduling' },
      { title: 'Patient reports severe tooth pain', tag: 'Escalation' },
    ],
    scriptType: 'Text Prompt',
    script: [
      'You are the AI receptionist for a dental office. You are friendly, calm, and reassuring.',
      '',
      'Your responsibilities:',
      '- Greet patients and help them book, reschedule, or confirm appointments.',
      '- Answer common questions about hours, location, services, and insurance you were given.',
      '- Collect the patient name, phone number, and reason for the visit.',
      '- For dental emergencies or clinical questions, reassure the patient and escalate to staff.',
      '',
      'Confirm dates and times back to the patient. Never give medical advice or quote prices you',
      'were not provided — offer to have a team member follow up.',
    ].join('\n'),
    settings: { tone: 'Friendly' },
  },
];

export const DemoAIAgents = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [collections, setCollections] = useState([]);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('updated'); // 'updated' | 'name'
  const [sortOpen, setSortOpen] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const [editAgent, setEditAgent] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError(null);
    try {
      const [aRes, nRes, cRes] = await Promise.all([
        agentsAPI.list(),
        phonenumbersAPI.list().catch(() => ({ data: { numbers: [] } })),
        knowledgeAPI.collections().catch(() => ({ data: { collections: [] } })),
      ]);
      setNumbers(nRes.data.numbers || []);
      setCollections(cRes.data.collections || []);
      setAgents(aRes.data.agents || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const phonesFor = (scriptId) =>
    (numbers || []).filter(
      (n) => String(n.inboundScriptId) === String(scriptId) || String(n.outboundScriptId) === String(scriptId)
    );

  const handleDelete = async (a) => {
    setMenuFor(null);
    if (!window.confirm(`Delete "${a.name}"? Its script is kept. This cannot be undone.`)) return;
    try {
      await agentsAPI.remove(a._id);
      setAgents((list) => (list || []).filter((x) => x._id !== a._id));
    } catch (e) {
      alert(`Delete failed: ${e.response?.data?.error || e.message}`);
    }
  };

  const handleDuplicate = async (a) => {
    setMenuFor(null);
    try {
      const res = await agentsAPI.save({
        name: `${a.name || 'Untitled'} (copy)`,
        description: a.description || '',
        icon: a.icon || 'bot',
        scriptId: a.scriptId || null,
        settings: a.settings || {},
        collectionIds: a.collectionIds || [],
        status: 'draft',
      });
      const created = res.data.agent || { _id: res.data.id, name: `${a.name || 'Untitled'} (copy)`, icon: a.icon || 'bot', scriptId: a.scriptId || null, settings: a.settings || {}, collectionIds: a.collectionIds || [], status: 'draft' };
      setAgents((list) => [created, ...(list || [])]);
    } catch (e) {
      alert(`Duplicate failed: ${e.response?.data?.error || e.message}`);
    }
  };

  // Quick create: open the popup on a draft agent. Nothing is saved until the user hits Save,
  // so abandoning it never leaves an "Untitled Agent" behind.
  const startQuickCreate = () => {
    setCreateOpen(false);
    setEditAgent({ _id: null, name: '', description: '', icon: 'bot', scriptId: null, settings: {}, collectionIds: [], status: 'draft' });
  };

  const enriched = (agents || []).map((a, i) => {
    const status = String(a.status || 'draft');
    return {
      ...a,
      _phones: phonesFor(a.scriptId),
      _statusLabel: status.charAt(0).toUpperCase() + status.slice(1),
      _channels: { voice: phonesFor(a.scriptId).length > 0, chat: true, sms: phonesFor(a.scriptId).length > 0, email: false },
      _look: iconOption(a.icon) || AGENT_PALETTE[i % AGENT_PALETTE.length],
      _desc: a.description || 'AI agent',
      _updated: a.updatedAt || a.createdAt,
    };
  });

  const rows = [...enriched].sort((a, b) =>
    sort === 'name'
      ? (a.name || '').localeCompare(b.name || '')
      : new Date(b._updated || 0) - new Date(a._updated || 0)
  );

  const onSaved = (saved) =>
    setAgents((list) => {
      const arr = list || [];
      return arr.some((x) => x._id === saved._id)
        ? arr.map((x) => (x._id === saved._id ? { ...x, ...saved } : x))
        : [saved, ...arr];
    });

  const initials = ((user?.name || user?.email || '?').trim()[0] || '?').toUpperCase();
  const userName = user?.name || (user?.email ? user.email.split('@')[0] : 'You');

  return (
    <div className="flex h-full flex-col">
      <PageTitle
        title="Conversational AI Agents"
        subtitle="Build, manage, and optimize your AI that powers customer operations."
        action={
          <div className="relative">
            <PrimaryButton onClick={() => setCreateOpen((o) => !o)}>
              <Plus size={17} /> Create AI Agent
              <ChevronDown size={15} className={`transition-transform ${createOpen ? 'rotate-180' : ''}`} />
            </PrimaryButton>
            {createOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setCreateOpen(false)} />
                <div className="absolute right-0 z-40 mt-2 w-[340px] rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
                  <button
                    onClick={startQuickCreate}
                    className="flex w-full items-start gap-3 rounded-xl p-3 text-left transition-colors hover:bg-slate-50"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                      <Plus size={17} strokeWidth={1.9} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13.5px] font-semibold">Quick create</span>
                      <span className="block text-[12px] text-slate-500">If you have a script ready, click here to proceed to the Settings and Knowledge.</span>
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setCreateOpen(false);
                      navigate('/dashboard/customer-ai/create');
                    }}
                    className="flex w-full items-start gap-3 rounded-xl p-3 text-left transition-colors hover:bg-slate-50"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                      <Sparkles size={17} strokeWidth={1.9} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13.5px] font-semibold">Create from scratch</span>
                      <span className="block text-[12px] text-slate-500">Guided wizard — choose a template or start blank, then build, test, and deploy.</span>
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        }
      />

      {/* Controls */}
      <div className="mt-5 flex shrink-0 flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">
            <SlidersHorizontal size={15} className="text-slate-400" /> Filters
          </button>
          <div className="relative">
            <button
              onClick={() => setSortOpen((o) => !o)}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
            >
              Sort: {sort === 'name' ? 'Name' : 'Recently updated'}
              <ChevronDown size={14} className="text-slate-400" />
            </button>
            {sortOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setSortOpen(false)} />
                <div className="absolute right-0 z-40 mt-2 w-[200px] rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                  {[
                    ['updated', 'Recently updated'],
                    ['name', 'Name (A–Z)'],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setSort(key);
                        setSortOpen(false);
                      }}
                      className={`block w-full rounded-lg px-3 py-2 text-left text-[13px] font-medium ${
                        sort === key ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <Card className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
        {error && <div className="p-6"><ErrorNote error={error} /></div>}
        {!error && agents === null && <div className="p-6"><LoadingRows label="Loading agents…" /></div>}
        {!error && agents !== null && (
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full text-left">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="border-b border-slate-100 text-[11.5px] font-semibold uppercase tracking-wide text-slate-400">
                  <th className="px-6 py-3">Customer AI</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Channels</th>
                  <th className="px-4 py-3">Last Updated</th>
                  <th className="px-4 py-3">Updated By</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center text-[13.5px] text-slate-400">
                      No agents yet — create your first Customer AI.
                    </td>
                  </tr>
                )}
                {rows.map((a) => {
                  const look = a._look;
                  const Icon = look.icon;
                  const isGlobal = a.ownerId === 'global' || a.ownerId === 'opinion-service-global';
                  return (
                    <tr key={a._id} className="group hover:bg-slate-50/60">
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-3">
                          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${look.tint}`}>
                            <Icon size={18} strokeWidth={1.9} />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-[13.5px] font-semibold">{a.name || 'Untitled'}</p>
                            <p className="mt-0.5 truncate text-[12px] text-slate-500">{a._desc}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <StatusDot tone={STATUS_TONE[a._statusLabel]} label={a._statusLabel} />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                          {AGENT_CHANNELS.filter((c) => a._channels[c.key]).map(({ key, icon: CIcon, color }) => (
                            <CIcon key={key} size={16} className={color} strokeWidth={1.9} />
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-[12.5px] text-slate-500">{timeAgo(a._updated)}</td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <span
                            className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ${
                              isGlobal ? 'bg-slate-200 text-slate-500' : 'bg-teal-600 text-white'
                            }`}
                          >
                            {isGlobal ? '↔' : initials}
                          </span>
                          <span className="text-[12.5px] text-slate-600">{isGlobal ? 'Shared' : userName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setEditAgent(a)}
                            className="rounded-lg border border-slate-200 bg-white px-3.5 py-1.5 text-[12.5px] font-semibold hover:bg-slate-50"
                          >
                            Edit
                          </button>
                          <div className="relative">
                            <button
                              onClick={() => setMenuFor(menuFor === a._id ? null : a._id)}
                              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                            {menuFor === a._id && (
                              <>
                                <div className="fixed inset-0 z-30" onClick={() => setMenuFor(null)} />
                                <div className="absolute right-0 z-40 mt-1 w-[160px] rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                                  <button
                                    onClick={() => {
                                      setMenuFor(null);
                                      setEditAgent(a);
                                    }}
                                    className="block w-full rounded-lg px-3 py-2 text-left text-[13px] font-medium text-slate-600 hover:bg-slate-50"
                                  >
                                    Edit agent
                                  </button>
                                  <button
                                    onClick={() => handleDuplicate(a)}
                                    className="block w-full rounded-lg px-3 py-2 text-left text-[13px] font-medium text-slate-600 hover:bg-slate-50"
                                  >
                                    Duplicate
                                  </button>
                                  <button
                                    onClick={() => handleDelete(a)}
                                    disabled={isGlobal}
                                    className="block w-full rounded-lg px-3 py-2 text-left text-[13px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editAgent && (
        <AgentEditModal
          agent={editAgent}
          collections={collections}
          onClose={() => setEditAgent(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export const DemoAIScripts = () => {
  const [scripts, setScripts] = useState(null);
  const [error, setError] = useState(null);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    scriptsAPI
      .list()
      .then((res) => setScripts(res.data.scripts || []))
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete script "${s.scriptName}"? This cannot be undone.`)) return;
    try {
      await scriptsAPI.remove(s._id);
      setScripts((list) => list.filter((x) => x._id !== s._id));
    } catch (e) {
      alert(`Delete failed: ${e.response?.data?.error || e.message}`);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageTitle
        title="Script Building"
        subtitle="The conversation scripts your Customer AI can run."
        action={
          <div className="relative">
            <PrimaryButton onClick={() => setNewMenuOpen((o) => !o)}>
                <Plus size={17} /> New Script
                <ChevronDown
                  size={15}
                  className={`transition-transform ${newMenuOpen ? 'rotate-180' : ''}`}
                />
              </PrimaryButton>
              {newMenuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setNewMenuOpen(false)} />
                  <div className="absolute right-0 z-40 mt-2 w-[340px] rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
                    <button
                      onClick={() => {
                        setNewMenuOpen(false);
                        navigate('/dashboard/customer-ai/scripts/new-prompt');
                      }}
                      className="flex w-full items-center gap-3.5 rounded-xl p-3 text-left transition-colors hover:bg-slate-50"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-sky-100 bg-sky-50 text-sky-600">
                        <MessageSquare size={18} strokeWidth={1.9} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-semibold">Start from scratch with text prompt</span>
                        <span className="mt-0.5 block text-[12.5px] text-slate-500">
                          Describe your AI in a few sentences and we'll build it for you.
                        </span>
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        setNewMenuOpen(false);
                        navigate('/dashboard/customer-ai/scripts/new-workflow');
                      }}
                      className="flex w-full items-center gap-3.5 rounded-xl p-3 text-left transition-colors hover:bg-slate-50"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-violet-100 bg-violet-50 text-violet-600">
                        <GitBranch size={18} strokeWidth={1.9} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-semibold">Start from scratch with workflow</span>
                        <span className="mt-0.5 block text-[12.5px] text-slate-500">
                          Map out the conversation flow step-by-step.
                        </span>
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          }
        />
        <Card className="mt-5 flex min-h-0 flex-1 flex-col overflow-hidden p-6">
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {error && <div className="mt-4"><ErrorNote error={error} /></div>}
          {!error && scripts === null && <LoadingRows label="Loading scripts…" />}
          {!error && scripts !== null && scripts.length === 0 && (
            <p className="py-10 text-center text-[13.5px] text-slate-400">
              No scripts yet — create your first one below.
            </p>
          )}
          <div className="divide-y divide-slate-100">
            {(scripts || []).map((s) => {
              const kind = scriptKind(s.type);
              const isGlobal = s.ownerId === 'global' || s.ownerId === 'opinion-service-global';
              return (
                <div
                  key={s._id}
                  onClick={() => navigate(`/dashboard/customer-ai/scripts/${s._id}`)}
                  className="flex cursor-pointer items-center gap-3.5 py-3.5 transition-colors hover:bg-slate-50/60"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                      kind === 'Workflow' ? 'bg-violet-50 text-violet-600' : 'bg-sky-50 text-sky-600'
                    }`}
                  >
                    {kind === 'Workflow' ? (
                      <GitBranch size={18} strokeWidth={1.9} />
                    ) : (
                      <FileText size={18} strokeWidth={1.9} />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-[14px] font-semibold">{s.scriptName || 'Untitled'}</span>
                      <Badge tone={kind === 'Workflow' ? 'purple' : 'blue'}>{kind}</Badge>
                      {isGlobal && <Badge tone="teal">Global</Badge>}
                    </div>
                    <p className="mt-0.5 text-[12.5px] text-slate-500">Created {timeAgo(s.createdAt)}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/customer-ai/scripts/${s._id}`);
                    }}
                    className="rounded-lg border border-slate-200 bg-white px-3.5 py-1.5 text-[12.5px] font-semibold hover:bg-slate-50"
                  >
                    <span className="flex items-center gap-1.5">
                      <Pencil size={13} /> Open
                    </span>
                  </button>
                  {!isGlobal && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(s);
                      }}
                      title="Delete script"
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* New script rows */}
          <div className="mt-4 space-y-3">
            <button
              onClick={() => navigate('/dashboard/customer-ai/scripts/new-prompt')}
              className="flex w-full items-center gap-3.5 rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-teal-300 hover:bg-teal-50/40"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-100 bg-sky-50 text-sky-600">
                <MessageSquare size={18} strokeWidth={1.9} />
              </span>
              <span className="flex-1">
                <span className="block text-[14px] font-semibold">Start from scratch with text prompt</span>
                <span className="mt-0.5 block text-[12.5px] text-slate-500">
                  Describe your AI in a few sentences and we'll build it for you.
                </span>
              </span>
              <ChevronRight size={17} className="text-slate-300" />
            </button>
            <button
              onClick={() => navigate('/dashboard/customer-ai/scripts/new-workflow')}
              className="flex w-full items-center gap-3.5 rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-teal-300 hover:bg-teal-50/40"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-violet-100 bg-violet-50 text-violet-600">
                <GitBranch size={18} strokeWidth={1.9} />
              </span>
              <span className="flex-1">
                <span className="block text-[14px] font-semibold">Start from scratch with workflow</span>
                <span className="mt-0.5 block text-[12.5px] text-slate-500">
                  Map out the conversation flow step-by-step.
                </span>
              </span>
              <ChevronRight size={17} className="text-slate-300" />
            </button>
          </div>
          </div>
        </Card>
    </div>
  );
};

/* ================= 3. Knowledge & Connections ================= */

const COLLECTION_TINTS = ['bg-teal-50 text-teal-600', 'bg-amber-50 text-amber-500', 'bg-orange-50 text-orange-500', 'bg-violet-50 text-violet-600'];

/* Integrations have no backend yet — display-only list, all shown as Not Connected. */
const CONNECTIONS = [
  { name: 'Salesforce', desc: 'Access CRM data, leads, and customer information', logo: '☁️', connected: false, on: false },
  { name: 'Google Calendar', desc: 'Check availability and schedule appointments', logo: '📅', connected: false, on: false },
  { name: 'Epic EHR', desc: 'Retrieve patient records and appointment details', logo: '🏥', connected: false, on: false },
  { name: 'Twilio', desc: 'Send SMS notifications and reminders', logo: '📞', connected: false, on: false },
  { name: 'Stripe', desc: 'Process payments and verify transactions', logo: '💳', connected: false, on: false },
  { name: 'Mailchimp', desc: 'Manage email campaigns and subscribers', logo: '📧', connected: false, on: false },
  { name: 'WhatsApp Business', desc: 'Send and receive WhatsApp messages', logo: '💬', connected: false, on: false },
];

/* Upload documents modal — ports the legacy UploadKnowledgeBase flow (POST /api/upload-docs). */
const AddKnowledgeModal = ({ open, onClose, collections, ownerId, onUploaded }) => {
  const [files, setFiles] = useState([]);
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [collectionName, setCollectionName] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const inputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setFiles([]);
    setCollectionName('');
    setCollectionId('');
    setMsg('');
    if (inputRef.current) inputRef.current.value = '';
  };
  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!files.length) return setMsg('Select at least one file.');
    if (mode === 'new' && !collectionName.trim()) return setMsg('Enter a collection name.');
    if (mode === 'existing' && !collectionId) return setMsg('Select a collection.');
    setUploading(true);
    setMsg('⏳ Uploading and generating embeddings… this can take up to a minute.');
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      if (mode === 'new') fd.append('collectionName', collectionName.trim());
      else fd.append('collectionId', collectionId);
      const res = await knowledgeAPI.upload(fd, ownerId);
      if (res.data?.success) {
        setMsg(`✅ ${res.data.message || 'Uploaded successfully.'}`);
        onUploaded?.();
        setTimeout(close, 1300);
      } else {
        setMsg(`❌ ${res.data?.message || 'Upload failed.'}`);
      }
    } catch (e) {
      setMsg(`❌ ${e.response?.data?.message || e.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={close}>
      <div className="w-full max-w-[460px] rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-[16px] font-semibold">Add Knowledge</h3>
          <button onClick={close} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        <p className="mt-1 text-[12.5px] text-slate-500">
          Upload documents your AI can use to answer questions. PDF, DOCX, XLSX, PPTX, TXT and more.
        </p>

        <button
          onClick={() => inputRef.current?.click()}
          className="mt-4 flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-4 py-6 text-center transition-colors hover:border-teal-300 hover:bg-teal-50/40"
        >
          <Upload size={22} className="text-teal-600" />
          <span className="text-[13px] font-semibold">Click to choose files</span>
          <span className="text-[12px] text-slate-400">You can select multiple files</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
        {files.length > 0 && (
          <div className="mt-2 max-h-24 space-y-1 overflow-y-auto">
            {files.map((f, i) => (
              <p key={i} className="flex items-center gap-1.5 truncate text-[12.5px] text-slate-600">
                <FileText size={13} className="shrink-0 text-slate-400" /> {f.name}
              </p>
            ))}
          </div>
        )}

        <div className="mt-4">
          <div className="flex gap-4 text-[13px] font-medium">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} className="accent-teal-600" />
              New collection
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                checked={mode === 'existing'}
                onChange={() => setMode('existing')}
                className="accent-teal-600"
              />
              Existing collection
            </label>
          </div>
          {mode === 'new' ? (
            <input
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              placeholder="e.g. Patient Access Docs"
              className="mt-2 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
            />
          ) : (
            <select
              value={collectionId}
              onChange={(e) => setCollectionId(e.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
            >
              <option value="">Select a collection</option>
              {(collections || []).map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {msg && <p className="mt-3 text-[12.5px] text-slate-600">{msg}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <SecondaryButton onClick={close}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={uploading}>
            {uploading ? 'Uploading…' : `Upload${files.length ? ` ${files.length} file(s)` : ''}`}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export const DemoAIKnowledge = () => {
  const { user } = useAuth();
  const [collections, setCollections] = useState(null);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [connections, setConnections] = useState(CONNECTIONS);
  const [kTab, setKTab] = useState('Collections');
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    Promise.all([knowledgeAPI.collections(), knowledgeAPI.files()])
      .then(([colRes, fileRes]) => {
        setCollections(colRes.data.collections || []);
        setFiles(fileRes.data.files || []);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ownerId = user?.id || user?._id || user?.userId || null;

  return (
    <div className="flex h-full flex-col">
      <AddKnowledgeModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        collections={collections}
        ownerId={ownerId}
        onUploaded={load}
      />
      <PageTitle
        title="Knowledge Base & Connections"
        subtitle="Connect knowledge sources and API connections your AI can access to get the right information and take action."
      />

      <div className="mt-6 grid min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-5 overflow-hidden xl:grid-cols-2">
        {/* Knowledge Sources */}
        <Card className="max-h-full overflow-y-auto p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-[15px] font-semibold">Knowledge Sources</h3>
            <button
              onClick={() => setAddOpen(true)}
              className="flex items-center gap-1.5 rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-teal-700 hover:bg-teal-50"
            >
              <Plus size={14} /> Add Knowledge
            </button>
          </div>
          <p className="mt-1 text-[12.5px] text-slate-500">
            Add the knowledge your AI will use to answer questions and provide accurate information.
          </p>

          <div className="mt-4 flex items-center gap-5 border-b border-slate-100 text-[13px] font-medium">
            {[`Collections (${collections?.length ?? '…'})`, `Files (${files.length})`].map((t) => {
              const key = t.startsWith('Collections') ? 'Collections' : 'Files';
              return (
                <button
                  key={key}
                  onClick={() => setKTab(key)}
                  className={`-mb-px border-b-2 pb-2.5 transition-colors ${
                    kTab === key ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>

          {error && <div className="mt-4"><ErrorNote error={error} /></div>}
          {!error && collections === null && <LoadingRows label="Loading knowledge…" />}
          {!error && collections !== null && (
            <div className="divide-y divide-slate-50">
              {kTab === 'Collections' &&
                collections.map((c, i) => (
                  <div key={c._id} className="flex items-center gap-3.5 py-3.5">
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                        COLLECTION_TINTS[i % COLLECTION_TINTS.length]
                      }`}
                    >
                      <Folder size={18} strokeWidth={1.9} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-[13.5px] font-semibold">{c.name}</span>
                        <Badge tone="gray">Collection</Badge>
                      </div>
                      <p className="mt-0.5 text-[12px] text-slate-500">
                        {c.fileCount ?? (c.fileIds || []).length} files • Updated {timeAgo(c.updatedAt || c.createdAt)}
                      </p>
                    </div>
                  </div>
                ))}
              {kTab === 'Files' &&
                files.map((f) => (
                  <div key={f._id} className="flex items-center gap-3.5 py-3.5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                      <FileText size={18} strokeWidth={1.9} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-semibold">{f.originalName}</span>
                      <p className="mt-0.5 text-[12px] text-slate-500">Uploaded {timeAgo(f.uploadedAt)}</p>
                    </div>
                  </div>
                ))}
              {((kTab === 'Collections' && collections.length === 0) || (kTab === 'Files' && files.length === 0)) && (
                <p className="py-8 text-center text-[13.5px] text-slate-400">
                  Nothing here yet — click “Add Knowledge” to upload documents.
                </p>
              )}
            </div>
          )}
        </Card>

        {/* Connections */}
        <Card className="max-h-full overflow-y-auto p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-[15px] font-semibold">API Connections</h3>
            <Badge tone="orange">Coming soon</Badge>
          </div>
          <p className="mt-1 text-[12.5px] text-slate-500">
            Connect tools and integrations to let your AI take action and get real-time data. These
            integrations are rolling out — request the ones you need and we'll enable them for your account.
          </p>

          <div className="mt-4 space-y-2">
            {connections.map(({ name, desc, logo }) => (
              <div key={name} className="flex items-center gap-3.5 rounded-xl border border-slate-100 p-3 opacity-90">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-100 bg-white text-[18px]">
                  {logo}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-semibold">{name}</p>
                  <p className="mt-0.5 truncate text-[12px] text-slate-500">{desc}</p>
                </div>
                <Badge tone="gray">Planned</Badge>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-indigo-50/60 p-3.5 text-[12.5px] leading-relaxed text-slate-600">
            <Info size={15} className="mt-0.5 shrink-0 text-indigo-400" />
            <span>
              Integrations aren't set up on your account yet. When you're ready to connect a tool
              (calendar, CRM, EHR, and more), reach out in Settings → Support and we'll enable it for you.
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ================= 4. Testing ================= */

const TEST_CHANNELS = [
  { key: 'voice', name: 'Voice', sub: 'Inbound & Outbound Calls', icon: Phone, tint: 'bg-teal-50 text-teal-600', enabled: true },
  { key: 'webvoice', name: 'Web Voice', sub: 'Talk in the browser', icon: Mic, tint: 'bg-fuchsia-50 text-fuchsia-600', enabled: true },
  { key: 'chat', name: 'Web Chat', sub: 'Live Chat Widget', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600', enabled: true },
  { key: 'sms', name: 'SMS', sub: 'Inbound & Outbound SMS', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600', enabled: false },
  { key: 'email', name: 'Email', sub: 'Inbound & Outbound Email', icon: Mail, tint: 'bg-orange-50 text-orange-500', enabled: false },
  { key: 'whatsapp', name: 'WhatsApp', sub: 'Business Account', icon: MessageCircle, tint: 'bg-emerald-50 text-emerald-600', enabled: false },
  { key: 'other', name: 'Other Channels', sub: 'Instagram, Messenger, etc.', icon: MoreHorizontal, tint: 'bg-slate-100 text-slate-500', enabled: false },
];

const ConfigRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-6 py-2">
    <span className="text-[13px] text-slate-500">{label}</span>
    <span className="text-right text-[13px] font-semibold">{value}</span>
  </div>
);

const HowTestingWorks = ({ lines }) => (
  <div className="mt-5 rounded-xl bg-indigo-50/60 p-4">
    <p className="flex items-center gap-2 text-[13px] font-semibold">
      <Info size={14} className="text-indigo-400" /> How testing works
    </p>
    <ul className="mt-2 space-y-1.5">
      {lines.map((l) => (
        <li key={l} className="flex items-start gap-2 text-[12.5px] text-slate-600">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-500" />
          {l}
        </li>
      ))}
    </ul>
  </div>
);

const VoiceTest = ({ scriptData, agent, numbers, agents, selAgentId, setSelAgentId }) => {
  const list = numbers || [];
  const [fromNumber, setFromNumber] = useState(DEFAULT_FROM_NUMBER);
  const [to, setTo] = useState('');
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const settings = agent?.settings || scriptData?.settings || {};
  const voiceLabel = VOICE_OPTIONS.find((v) => v.id === settings.voiceId)
    ? formatAgentVoiceLabel(VOICE_OPTIONS.find((v) => v.id === settings.voiceId))
    : 'Agent default';
  const ttsEngine = settings.ttsProvider === 'elevenlabs' || settings.provider === 'ElevenLabs' ? 'elevenlabs' : 'kokoro';

  const start = async () => {
    setStarting(true);
    setErr('');
    setOk('');
    try {
      const data = await placeTestCall({ scriptData, to, from: fromNumber, agentId: selAgentId || undefined });
      setOk(`Call started. Call SID: ${data.callSid}`);
      setTo('');
    } catch (e) {
      setErr(e.message);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="flex h-full flex-col overflow-y-auto rounded-2xl border border-slate-200 p-5">
        <h3 className="text-[15px] font-semibold">Test Setup</h3>
        <p className="mt-1 text-[12.5px] text-slate-500">Pick the agent to test — its voice and script settings are used automatically.</p>
        <div className="mt-4 space-y-3.5">
          <div>
            <label className="text-[12.5px] font-medium text-slate-500">Customer AI (agent)</label>
            <ListPicker
              maxHeight={200}
              value={selAgentId}
              onChange={setSelAgentId}
              options={(agents || []).map((a) => ({
                value: String(a._id),
                label: a.name || 'Untitled',
                sub: a.scriptId ? undefined : 'No script linked',
              }))}
            />
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-500">Phone Number (From)</label>
            <ListPicker
              maxHeight={200}
              value={fromNumber}
              onChange={setFromNumber}
              options={[
                { value: DEFAULT_FROM_NUMBER, label: `${DEFAULT_FROM_NUMBER}`, sub: 'Default' },
                ...list.map((n) => ({ value: n.number, label: n.number, sub: n.friendlyName || undefined })),
              ]}
            />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-[12.5px] text-slate-600">
            <p className="flex items-center justify-between gap-3">
              <span className="text-slate-500">Voice</span>
              <span className="font-semibold">{voiceLabel}</span>
            </p>
            <p className="mt-1.5 flex items-center justify-between gap-3">
              <span className="text-slate-500">Voice model</span>
              <span className="font-semibold">{ttsEngine === 'elevenlabs' ? 'ElevenLabs' : 'Kokoro'}</span>
            </p>
            <p className="mt-2 text-[11.5px] text-slate-400">Change these in Customer AI › Agents.</p>
          </div>
        </div>
        <HowTestingWorks
          lines={[
            'Enter your phone number and start the test call.',
            'Our AI calls you and runs the selected agent’s script and voice.',
            'The conversation is saved to Logs when the call ends.',
          ]}
        />
      </div>

      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 p-5">
        <h3 className="shrink-0 text-[15px] font-semibold">Place a test call</h3>
        <p className="mt-1 shrink-0 text-[12.5px] text-slate-500">Enter a phone number to receive a call from your AI.</p>
        <label className="mt-4 block shrink-0 text-[12.5px] font-medium text-slate-500">Destination number</label>
        <input
          type="tel"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="+1 555 555 1234"
          className="mt-1.5 w-full shrink-0 rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
        />
        <PrimaryButton className="mt-4 w-full shrink-0" onClick={start} disabled={!to.trim() || starting || !scriptData}>
          <Phone size={16} /> {starting ? 'Starting…' : 'Start Test Call'}
        </PrimaryButton>
        {!scriptData && (
          <p className="mt-2 shrink-0 text-center text-[12px] text-slate-400">
            Select a Customer AI in Voice Configuration to enable testing.
          </p>
        )}
        {err && (
          <div className="mt-3 shrink-0 rounded-xl border border-red-100 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-600">
            {err}
          </div>
        )}
        {ok && (
          <div className="mt-3 shrink-0 rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-700">
            {ok}
          </div>
        )}
        <LiveTranscript className="mt-4 min-h-[180px] flex-1" />
      </div>
    </div>
  );
};

const ChatTest = ({ scriptData, agents, selAgentId, setSelAgentId }) => {
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Widget customization (mirrors the legacy embed widget options)
  const [title, setTitle] = useState('How can we help you?');
  const [subtitle, setSubtitle] = useState('We typically reply in a few seconds');
  const [welcome, setWelcome] = useState("Hi! I'm here to help you with any questions you have.");
  const [primaryColor, setPrimaryColor] = useState('#0D9488');
  const [position, setPosition] = useState('right'); // 'right' | 'left'
  const [placeholder, setPlaceholder] = useState('Type your message…');

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  // Run the SAME workflow node engine the phone call uses (via the TEN bridge), so the chat
  // follows the script exactly — greeting node, menu, transitions — instead of a loose LLM chat.
  const start = async () => {
    const agent = (agents || []).find((a) => String(a._id) === String(selAgentId));
    const scriptId = agent?.scriptId ? String(agent.scriptId) : (scriptData?._id ? String(scriptData._id) : null);
    if (!scriptId && !scriptData?.script) {
      setMessages([{ role: 'assistant', content: 'Pick an agent that has a script linked first.' }]);
      return;
    }
    setSending(true);
    setMessages([]);
    try {
      const res = await fetch(`${getOutboundCallBaseUrl()}/api/ten/session-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(scriptId ? { scriptId } : { scriptData }),
          callerNumber: 'webchat',
          ...(getCurrentUserIdForCalls() ? { placedByUserId: getCurrentUserIdForCalls() } : {}),
        }),
      });
      const data = await res.json();
      if (data?.success && data.sessionId) {
        setSessionId(data.sessionId);
        setMessages([{ role: 'assistant', content: data.greeting || welcome }]);
      } else {
        setMessages([{ role: 'assistant', content: `Error: ${data?.message || 'could not start session'}` }]);
      }
    } catch (e) {
      setMessages([{ role: 'assistant', content: `Error: ${e.message}` }]);
    } finally {
      setSending(false);
      setInput('');
    }
  };

  const fieldClass =
    'mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[13.5px] outline-none focus:border-teal-500';

  const send = async () => {
    const q = input.trim();
    if (!q || sending || !sessionId) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setSending(true);
    try {
      const res = await fetch(`${getOutboundCallBaseUrl()}/api/ten/turn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, transcript: q }),
      });
      const data = await res.json();
      if (data?.success) {
        if (data.message) setMessages((m) => [...m, { role: 'assistant', content: data.message }]);
        if (data.end) {
          setMessages((m) => [...m, { role: 'assistant', content: '— conversation ended —' }]);
          setSessionId('');
        }
      } else {
        setMessages((m) => [...m, { role: 'assistant', content: `Error: ${data?.message || 'request failed'}` }]);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: `Error: ${e.message}` }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="flex h-full flex-col overflow-y-auto rounded-2xl border border-slate-200 p-5">
        <h3 className="text-[15px] font-semibold">Web Chat Configuration</h3>
        <p className="mt-1 text-[12.5px] text-slate-500">Customize the widget and choose the agent it runs.</p>
        <div className="mt-4 space-y-3.5">
          <div>
            <label className="text-[12.5px] font-medium text-slate-500">Customer AI (agent)</label>
            <ListPicker
              maxHeight={180}
              value={selAgentId}
              onChange={setSelAgentId}
              options={(agents || []).map((a) => ({
                value: String(a._id),
                label: a.name || 'Untitled',
                sub: a.scriptId ? undefined : 'No script linked',
              }))}
            />
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-500">Widget Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={fieldClass} />
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-500">Subtitle</label>
            <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className={fieldClass} />
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-500">Welcome Message</label>
            <textarea
              rows={2}
              value={welcome}
              onChange={(e) => setWelcome(e.target.value)}
              className={`${fieldClass} resize-none`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[12.5px] font-medium text-slate-500">Primary Color</label>
              <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 px-2.5 py-1.5">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-7 w-9 cursor-pointer rounded border-0 bg-transparent p-0"
                />
                <input
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-full bg-transparent text-[13px] outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-[12.5px] font-medium text-slate-500">Position</label>
              <ListPicker
                columns={2}
                value={position}
                onChange={setPosition}
                options={[
                  { value: 'right', label: 'Bottom Right' },
                  { value: 'left', label: 'Bottom Left' },
                ]}
              />
            </div>
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-500">Input Placeholder</label>
            <input value={placeholder} onChange={(e) => setPlaceholder(e.target.value)} className={fieldClass} />
          </div>
        </div>
      </div>

      <div className="flex h-full flex-col rounded-2xl border border-slate-200 p-5">
        <h3 className="shrink-0 text-[15px] font-semibold">Live preview</h3>
        <p className="mt-1 shrink-0 text-[12.5px] text-slate-500">Chat with your AI and preview the widget styling.</p>

        {/* faux website frame — the widget docks in the corner like a real site */}
        <div className="relative mt-4 min-h-[380px] flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="flex items-center gap-1.5 border-b border-slate-200 bg-white/70 px-3 py-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
            <span className="ml-2 rounded bg-slate-200/70 px-2 py-0.5 text-[10px] text-slate-400">yourwebsite.com</span>
          </div>

          <div
            className={`absolute bottom-4 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
              position === 'left' ? 'left-4' : 'right-4'
            } ${sessionId ? 'h-[calc(100%-4rem)] w-[calc(100%-2rem)]' : 'h-[360px] w-[300px]'}`}
          >
            <div className="flex items-center gap-2.5 px-4 py-3 text-white" style={{ backgroundColor: primaryColor }}>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
              <Bot size={17} />
            </span>
            <div className="min-w-0">
              <p className="truncate text-[13.5px] font-semibold">{title || 'Chat'}</p>
              {subtitle && <p className="truncate text-[11px] text-white/80">{subtitle}</p>}
            </div>
          </div>

          <div ref={scrollRef} className="min-h-0 flex-1 space-y-2.5 overflow-y-auto bg-slate-50/50 p-4">
            {!sessionId && (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <p className="text-[13px] font-semibold">Start a chat</p>
                <button
                  onClick={start}
                  disabled={!scriptData}
                  style={{ backgroundColor: primaryColor }}
                  className="mt-2 rounded-xl px-4 py-2 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                >
                  Start session
                </button>
                {!scriptData && <p className="mt-2 text-[11.5px] text-slate-400">Select a Customer AI first.</p>}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[12.5px] leading-relaxed ${
                    m.role === 'user'
                      ? 'rounded-br-sm text-white'
                      : 'rounded-bl-sm bg-white text-slate-700 shadow-sm ring-1 ring-slate-100'
                  }`}
                  style={m.role === 'user' ? { backgroundColor: primaryColor } : undefined}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && <p className="text-[11.5px] text-slate-400">Typing…</p>}
          </div>

          {sessionId && (
            <div className="flex items-center gap-2 border-t border-slate-100 bg-white px-3 py-2.5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder={placeholder}
                disabled={sending}
                className="w-full bg-transparent text-[12.5px] outline-none"
              />
              <button
                onClick={send}
                disabled={!input.trim() || sending}
                style={{ color: primaryColor }}
                className="disabled:opacity-40"
              >
                <Send size={15} />
              </button>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Web Voice: talk to the agent in the browser (mic → server STT → node engine → agent voice).
const WebVoiceTest = ({ agents, selAgentId, setSelAgentId }) => {
  const [fillers, setFillers] = useState(false);
  return (
  <div className="grid h-full min-h-0 grid-cols-1 gap-5 lg:grid-cols-2">
    <div className="flex h-full flex-col overflow-y-auto rounded-2xl border border-slate-200 p-5">
      <h3 className="text-[15px] font-semibold">Test Setup</h3>
      <p className="mt-1 text-[12.5px] text-slate-500">Pick the agent, then talk to it in your browser — it uses the agent&apos;s script, knowledge, and voice.</p>
      <div className="mt-4">
        <label className="text-[12.5px] font-medium text-slate-500">Customer AI (agent)</label>
        <ListPicker
          maxHeight={220}
          value={selAgentId}
          onChange={setSelAgentId}
          options={(agents || []).map((a) => ({ value: String(a._id), label: a.name || 'Untitled', sub: a.scriptId ? undefined : 'No script linked' }))}
        />
      </div>
      <label className="mt-4 flex items-center gap-2 text-[12.5px] text-slate-600">
        <input type="checkbox" checked={fillers} onChange={(e) => setFillers(e.target.checked)} className="h-4 w-4 accent-teal-600" />
        AI-placed fillers (um / well / hmm — in context, ~1 in 3 sentences)
      </label>
      <p className="mt-3 text-[11.5px] text-slate-400">Allow microphone access when prompted. Works best in Chrome over HTTPS.</p>
    </div>
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 p-3">
      {selAgentId
        ? <BrowserVoiceWidget key={`${selAgentId}:${fillers}`} agentId={selAgentId} extraStart={{ fillers, fillerMode: 'ai' }} />
        : <div className="grid h-full place-items-center text-[13px] text-slate-400">Select an agent to start.</div>}
    </div>
  </div>
  );
};

export const DemoAITesting = () => {
  const [selected, setSelected] = useState('voice');
  const [agents, setAgents] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [selAgentId, setSelAgentId] = useState('');
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const current = TEST_CHANNELS.find((c) => c.key === selected);

  useEffect(() => {
    Promise.all([agentsAPI.list(), phonenumbersAPI.list()])
      .then(([aRes, nRes]) => {
        const list = aRes.data.agents || [];
        setAgents(list);
        setNumbers(nRes.data.numbers || []);
        const first = list.find((a) => a.scriptId) || list[0];
        if (first) setSelAgentId(String(first._id));
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const selAgent = agents.find((a) => String(a._id) === String(selAgentId)) || null;

  useEffect(() => {
    const sid = selAgent?.scriptId;
    if (!sid) return setDoc(null);
    scriptsAPI
      .get(sid)
      .then((res) => setDoc(res.data.script))
      .catch(() => setDoc(null));
  }, [selAgent?.scriptId]);

  // The agent owns voice/model/tone; merge its settings onto the linked script for the test run.
  const scriptData = doc
    ? { _id: doc._id, scriptName: doc.scriptName, script: doc.script, type: doc.type, settings: selAgent?.settings || doc.settings || {} }
    : null;

  return (
    <div className="flex h-full flex-col">
      <PageTitle
        title="Testing"
        subtitle="Test each channel before your Customer AI goes live."
      />

      {error && <div className="mt-4"><ErrorNote error={error} /></div>}

      <div className="mt-6 grid min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-5 overflow-hidden xl:grid-cols-[300px_1fr]">
        {/* Channels list */}
        <Card className="h-full overflow-y-auto p-5">
          <h2 className="text-[16px] font-semibold">Channels</h2>
          <p className="mt-1 text-[12.5px] leading-snug text-slate-500">
            Voice and Web Chat are ready to test. Other channels are coming soon.
          </p>
          <div className="mt-4 space-y-2.5">
            {TEST_CHANNELS.map(({ key, name, sub, icon: Icon, tint, enabled }) => (
              <button
                key={key}
                onClick={() => enabled && setSelected(key)}
                disabled={!enabled}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                  !enabled
                    ? 'cursor-not-allowed border-slate-100 opacity-60'
                    : selected === key
                    ? 'border-teal-500 bg-teal-50/40'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                  <Icon size={17} strokeWidth={1.9} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-[13.5px] font-semibold">{name}</span>
                    <Badge tone={enabled ? 'green' : 'gray'}>{enabled ? 'Enabled' : 'Disabled'}</Badge>
                  </span>
                  <span className="mt-0.5 block truncate text-[11.5px] text-slate-500">{sub}</span>
                </span>
                {enabled ? (
                  <CheckCircle2 size={17} className="shrink-0 text-emerald-500" />
                ) : (
                  <span className="h-[17px] w-[17px] shrink-0 rounded-full bg-slate-200" />
                )}
                <ChevronRight size={15} className="shrink-0 text-slate-300" />
              </button>
            ))}
          </div>
        </Card>

        {/* Test panel */}
        <Card className="flex h-full min-h-0 flex-col overflow-hidden p-6">
          <div className="flex shrink-0 items-center gap-2.5">
            <h2 className="text-[17px] font-semibold">Test {current.name}</h2>
            <Badge tone={current.enabled ? 'green' : 'gray'}>{current.enabled ? 'Enabled' : 'Disabled'}</Badge>
          </div>
          <p className="mt-1 shrink-0 text-[13px] text-slate-500">
            Test your {current.name.toLowerCase()} channel to see how your AI responds.
          </p>

          <div className="mt-5 min-h-0 flex-1 overflow-y-auto">
            {selected === 'voice' && (
              <VoiceTest
                scriptData={scriptData}
                agent={selAgent}
                numbers={numbers}
                agents={agents}
                selAgentId={selAgentId}
                setSelAgentId={setSelAgentId}
              />
            )}
            {selected === 'webvoice' && (
              <WebVoiceTest agents={agents} selAgentId={selAgentId} setSelAgentId={setSelAgentId} />
            )}
            {selected === 'chat' && (
              <ChatTest
                scriptData={scriptData}
                agents={agents}
                selAgentId={selAgentId}
                setSelAgentId={setSelAgentId}
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
